<template>
  <div class="google-login-container">
      <!-- 非alleat域名直接显示webview -->
      <div v-if="shouldUseWebviewLogin" class="webview-container">
        <!-- 骨架屏加载状态 -->
        <div v-if="!webviewLoaded" class="webview-skeleton">
          <div class="skeleton-content">
            <div class="skeleton-icon"></div>
            <div class="skeleton-text"></div>
          </div>
        </div>
        <!-- webview iframe -->
        <iframe 
          v-show="webviewLoaded"
          :src="webviewUrl"
          class="webview-iframe"
          frameborder="0"
          @load="onWebviewLoad"
          @error="onWebviewError"
        ></iframe>
      </div>
    
    <!-- alleat域名显示按钮 -->
    <button 
      v-else
      class="google-login-btn" 
      @click="handleGoogleLogin"
      :disabled="loading"
    >
      <i v-if="loading" class="el-icon-loading"></i>
      <img v-else src="@/assets/icon/google.png" alt="Google" class="google-icon">
      <span>{{ loading ? 'Signing in...' : 'Continue with Google' }}</span>
    </button>
  </div>
</template>

<script>
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '@/firebase.js';
import { postGateway } from '@/request';
import bus from '@/utils/bus.js';
import { shouldUseWebviewLogin } from '@/utils/domain-helper.js';
import { getWebUrl } from '@/config';

export default {
  name: 'GoogleLogin',
  props: {
    fromPage: {
      type: String,
      default: null
    },
  },
  data() {
    return {
      loading: false,
      webviewUrl: '',
      webviewLoaded: false
    }
  },
  computed: {
    shouldUseWebviewLogin() {
      return shouldUseWebviewLogin();
    },
    isAndroid() {
      return /Android/i.test(navigator.userAgent);
    }
  },
  mounted() {
    // 如果是webview模式，准备webview URL
    if (this.shouldUseWebviewLogin) {
      this.setupWebview();
      // 监听来自webview的消息
      window.addEventListener('message', this.handleWebviewMessage);
    }
  },
  beforeDestroy() {
    // 清理事件监听器
    if (this.shouldUseWebviewLogin) {
      window.removeEventListener('message', this.handleWebviewMessage);
    }
  },
  methods: {
    getWebUrl,
    async handleGoogleLogin() {
      
      this.loading = true;
      
      try {
        await this.handleDirectLogin();
      } catch (error) {
        console.error('Google login failed:', error);
        this.handleLoginError(error);
      } finally {
        this.loading = false;
      }
    },

    // 直接登录（alleatapp.com域名）
    async handleDirectLogin() {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      console.log('Google login successful:', user);
      
      // 检查邮箱是否存在，从多个来源获取邮箱
      let email = user.email;
      if (!email && user.providerData && user.providerData.length > 0) {
        email = user.providerData[0].email;
      }
      if (!email && user.reloadUserInfo && user.reloadUserInfo.providerUserInfo?.[0]?.email) {
        email = user.reloadUserInfo.providerUserInfo?.[0]?.email;
      }
      
      if (!email) {
        this.$message.error('Email is required for Google login. Please ensure your Google account has an email address.');
        throw new Error('Email is required for Google login');
      }
      
      // 发送用户信息到后端
      await this.sendUserToBackend(user);
    },

    
    async sendUserToBackend(user) {
      try {
        // 获取Google ID Token
        const idToken = await user.getIdToken();
        
        // 获取邮箱，从多个来源检查
        let email = user.email;
        if (!email && user.providerData && user.providerData.length > 0) {
          email = user.providerData[0].email;
        }
        if (!email && user.reloadUserInfo && user.reloadUserInfo.providerUserInfo && user.reloadUserInfo.providerUserInfo[0]) {
          email = user.reloadUserInfo.providerUserInfo[0].email;
        }
        
        const response = await postGateway({
          url: '/oauth/appGoogleLogin',
          method: 'POST',
          data: {
            idToken: idToken,
            email: email,
            firstName: user.displayName,
            photoUrl: user.photoURL,
            googleId: user.uid,
            render: 'google',
            type: 'web'
          }
        });
        
        if (response.code === 200 && response.data) {
          // 处理登录成功
          this.handleLoginSuccess(response.data);
          
        } else {
          throw new Error(response.msg || 'Login failed');
        }
        
      } catch (error) {
        console.error('Backend login failed:', error);
        this.$message.error(error.message || 'Login failed. Please try again.');
        throw error;
      }
    },
    
    // 完成登录流程
    completeLogin() {
      if (this.fromPage === 'checkout') {
        this.$emit('loginDone', true);
      } else if (this.fromPage === 'login') {
        this.$emit('loginDone', true);
      } else {
        // 默认跳转到首页
        this.$router.push('/');
      }
    },
    
    async checkAndBindPhoneIfNeeded() {
      try {
        // 通过用户详情接口获取最新的用户资料
        const detail = await this.fetchUserDetail();
        const contactPhone = (detail && detail.contactPhone) ? String(detail.contactPhone).trim() : '';
        
        if (!contactPhone) {
          // 需要绑定手机号
          if (this.fromPage === 'checkout') {
            // 在checkout页面时，发出事件让父组件处理
            this.$emit('needBindPhone');
          } else {
            // 其他页面跳转到绑定页面
            this.$router.replace({
              path: '/bind-phone',
              query: { from: this.fromPage }
            });
          }
          return true;
        }
        return false;
      } catch (error) {
        console.error('Check phone binding error:', error);
        return false;
      }
    },

    async fetchUserDetail() {
      const clientId = localStorage.getItem('clientId');
      if (!clientId) {
        return {};
      }
      const res = await postGateway({
        url: '/customerApp/center/getDetail',
        method: 'GET',
        data: {
          clientId
        }
      });
      return res && res.data ? res.data : {};
    },

    // 处理登录成功
    async handleLoginSuccess(loginData) {
      // 保存登录信息
      if (loginData.token) {
        localStorage.setItem('token', loginData.token);
        localStorage.setItem('refreshToken', loginData.refreshToken);
        localStorage.setItem('clientId', loginData.clientId);
        localStorage.setItem('aeClient', loginData.clientId);
        localStorage.setItem('userInfo', JSON.stringify(loginData));
        
        // 通知header登录了
        bus.$emit('login');
        
        // 显示成功消息
        this.$message.success('Login successful!');
        
        // 检查是否需要绑定手机号
        const needBindPhone = await this.checkAndBindPhoneIfNeeded(loginData);
        
        // 如果不需要绑定手机号，完成登录流程
        if (!needBindPhone) {
          this.completeLogin();
        }
      }
    },

    // 处理登录错误
    handleLoginError(error) {
      let errorMessage = 'Google login failed. Please try again.';
      
      if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = 'An account already exists with this email using a different login method.';
        this.$emit('needAlternativeLogin');
      } else if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Login was cancelled. Please try again.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup was blocked. Please allow popups and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      this.$message.error(errorMessage);
    },
    
    continueWithRedirect() {
      // 根据来源页面进行跳转
      if (this.fromPage === 'checkout') {
        this.$emit('loginDone', true);
      } else if (this.fromPage === 'login') {
        // 从登录页面跳转，优先从路由查询参数获取跳转路径
        const redirectPath = this.$route.query.redirect || this.$route.query.returnUrl;
        if (redirectPath) {
          this.$router.push(redirectPath);
        } else {
          // 如果没有指定跳转路径，返回上一页
          if (window.history.length > 2) {
            this.$router.go(-1);
          } else {
            this.$router.push('/');
          }
        }
      } else {
        // 默认跳转到首页
        this.$router.push('/');
      }
    },
    
    // 获取网站基础 URL
    getWebBaseUrl() {
      return this.getWebUrl();
    },
    // 设置webview
    setupWebview() {
      const baseUrl = this.getWebBaseUrl();
      const isMobile = window.innerWidth < 950;
      this.webviewUrl = `${baseUrl}/firebase-login-webview?type=google&embedded=true&mobile=${isMobile}`;
    },
    
    
    // webview加载完成
    onWebviewLoad() {
      console.log('Google login webview loaded');
    },
    
    // webview加载错误
    onWebviewError() {
      console.error('Google login webview failed to load');
      this.$message.error('Failed to load Google login. Please try again.');
    },
    
    // 处理来自webview的消息
    handleWebviewMessage(event) {
      console.log('收到webview消息:', event.origin, event.data);
      
      // 允许跨域通信，但验证消息格式
      if (!event.data || typeof event.data !== 'object') {
        console.log('消息格式不正确，忽略');
        return;
      }
      
      // 处理webview加载完成消息
      if (event.data.type === 'WEBVIEW_LOADED' && event.data.loginType === 'google') {
        console.log('Google webview loaded successfully');
        this.webviewLoaded = true;
        return;
      }
      
      // 只处理Google登录相关的消息
      if (event.data.type === 'LOGIN_SUCCESS' && event.data.loginType === 'google') {
        console.log('Google login success from webview:', event.data);
        this.loading = false;
        this.handleLoginSuccess(event.data.data);
      } else if (event.data.type === 'LOGIN_ERROR' && event.data.loginType === 'google') {
        console.error('Google login error from webview:', event.data.error);
        this.loading = false;
        this.$message.error(event.data.error || 'Google login failed');
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.google-login-container {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 15px;
  border-radius: 8px;
  overflow: hidden;
}

.google-login-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 300px;
  height: 48px;
  background: #fff;
  border: 1px solid #dadce0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #3c4043;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:hover:not(:disabled) {
    background: #f8f9fa;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .google-icon {
    width: 20px;
    height: 20px;
    margin-right: 12px;
  }
  
  .el-icon-loading {
    margin-right: 8px;
    font-size: 16px;
  }
}

// Webview样式
.webview-container {
  width: 100%;
  height: 56px; /* 与按钮高度一致 */
  overflow: hidden;
  position: relative;
  
  .webview-iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
  
  .webview-skeleton {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fafafa;
    border-radius: 8px;
    
    .skeleton-content {
      display: flex;
      align-items: center;
      width: 100%;
      max-width: 300px;
      padding: 0 16px;
      
      .skeleton-icon {
        width: 20px;
        height: 20px;
        background: #e8e8e8;
        border-radius: 4px;
        margin-right: 12px;
        animation: skeleton-pulse 2s ease-in-out infinite;
      }
      
      .skeleton-text {
        flex: 1;
        height: 16px;
        background: #e8e8e8;
        border-radius: 4px;
        animation: skeleton-pulse 2s ease-in-out infinite;
      }
    }
  }
}

// 移动端适配
@media (max-width: 480px) {
  .google-login-btn {
    height: 44px;
    font-size: 13px;
    
    .google-icon {
      width: 18px;
      height: 18px;
      margin-right: 10px;
    }
  }
  
  .webview-container {
    height: 44px; /* 移动端与按钮高度一致 */
    
    .webview-skeleton {
      .skeleton-content {
        .skeleton-icon {
          width: 18px;
          height: 18px;
        }
        
        .skeleton-text {
          height: 14px;
        }
      }
    }
  }
}

@keyframes skeleton-pulse {
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.3;
  }
  100% {
    opacity: 0.6;
  }
}
</style>
